"use client";

import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { ColumnManagerProvider, useColumnManager } from "@/lib/column-manager";
import { createColumnsFromConfig } from "@/lib/utils/columnFactory";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { EntityPageHeader } from "./EntityPageHeader";
import { EntityPageTable } from "./EntityPageTable";
import { EntityPageModals } from "./EntityPageModals";
import { SavedViewsTabs } from "./SavedViewsTabs";
import { useUniversalEntityPageContext } from "../contexts/UniversalEntityPageContext";
import {
  UniversalEntityPageDataProvider,
  useUniversalEntityPageDataContext,
} from "../contexts/UniversalEntityPageDataContext";
import {
  UniversalEntityPageHandlersProvider,
  useUniversalEntityPageHandlersContext,
} from "../contexts/UniversalEntityPageHandlersContext";
import {
  UniversalEntityPageUIProvider,
  useUniversalEntityPageUIContext,
} from "../contexts/UniversalEntityPageUIContext";
import { UniversalEntityPageCallbacksProvider } from "../contexts/UniversalEntityPageCallbacksContext";
import {
  UniversalEntityPageStaticProvider,
  useUniversalEntityPageStaticContext,
} from "../contexts/UniversalEntityPageStaticContext";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { UniversalEntityPageProps } from "../types";

/**
 * Inner component that uses the ColumnManager context
 * This must be inside the ColumnManagerProvider
 *
 * Memoized to prevent re-renders when parent context changes but props haven't changed.
 * This is the PRIMARY memoization layer that prevents full page remounts during
 * search/filter operations. The context providers above handle granular updates.
 */
function UniversalEntityPageContentInner<T extends BaseEntity>(props: UniversalEntityPageProps<T>) {
  const {
    entityName,
    config,
    hooks,
    title,
    description,
    showUserIcon = false,
    storageKey,
    showHeader = true,
    customHeader: CustomHeader,
    headerProps,
  } = props;

  // Read ONLY the slices this component renders from. Reading the whole god
  // context here meant an unrelated slice (saved views, bulk actions, export
  // progress) re-rendered the entire page body.
  const { pageConfig } = useUniversalEntityPageStaticContext<T>();
  const { dataState } = useUniversalEntityPageDataContext<T>();
  const { filterState } = useUniversalEntityPageUIContext<T>();
  const handlers = useUniversalEntityPageHandlersContext<T>();
  const columnManager = useColumnManager();

  // Enhance companies config with actual technology data when available
  const enhancedConfig = useMemo(() => {
    if (entityName !== "companies" || !config.fields?.current_technologies) {
      return config;
    }

    // Extract technologies from the actual data
    const techSet = new Set<string>();
    (dataState.data?.data || []).forEach((company: any) => {
      if (company?.current_technologies && typeof company.current_technologies === "object") {
        Object.keys(company.current_technologies).forEach((tech) => {
          if (tech) techSet.add(tech);
        });
      }
    });

    const technologies = Array.from(techSet).sort();

    if (technologies.length === 0) {
      return config;
    }

    const enhancedFields = { ...config.fields };
    enhancedFields.current_technologies = {
      ...enhancedFields.current_technologies,
      options: technologies.map((tech) => ({
        label: tech,
        value: tech,
      })),
    };

    return {
      ...config,
      fields: enhancedFields,
    };
  }, [config, dataState.data?.data, entityName]);

  // Apply visible columns when saved view changes
  useEffect(() => {
    if (filterState.activeViewId && config.savedViews && columnManager) {
      const savedView = config.savedViews.find((v) => v.id === filterState.activeViewId);
      if (savedView && savedView.visibleColumns) {
        // Update column visibility based on saved view
        const updatedColumns = columnManager.columns.map((col: any) => ({
          ...col,
          visible: savedView.visibleColumns!.includes(col.id),
        }));
        columnManager.setColumns(updatedColumns);
      }
    }
  }, [filterState.activeViewId, config.savedViews]);

  // Memoize computed values to prevent unnecessary re-renders of child components
  const storageKeyForColumns = useMemo(
    () => storageKey || `${entityName}-column-preferences`,
    [storageKey, entityName]
  );

  const displayTitle = useMemo(() => title || config.displayName, [title, config.displayName]);

  return (
    <div className="h-full flex flex-col bg-card-50">
      <div className="flex-1 flex flex-col min-h-0 mx-auto w-full px-4 sm:px-6 lg:px-8 py-2">
        {/* Saved Views Tabs */}
        {config.savedViews && config.savedViews.length > 0 && (
          <SavedViewsTabs
            savedViews={config.savedViews}
            activeViewId={filterState.activeViewId}
            onViewChange={filterState.applySavedView}
            displayName={displayTitle}
          />
        )}

        {/* Header Component */}
        <EntityPageHeader
          showHeader={showHeader}
          CustomHeader={CustomHeader}
          displayTitle={displayTitle}
          showUserIcon={showUserIcon}
          totalCount={dataState.data?.totalCount}
          onCreateItem={handlers.handleCreateItem}
          onImportItems={handlers.handleImportItems}
          onExportItems={handlers.handleExportItems}
          onComposeMessage={handlers.handleComposeMessage}
          hasComposeModal={!!pageConfig.customComponents?.composeModal}
          hasData={!!(dataState.data?.data && dataState.data.data.length > 0)}
          onAdminAction={headerProps?.onAdminAction}
          adminActionLabel={headerProps?.adminActionLabel}
          isAdmin={headerProps?.isAdmin}
        />

        {/* Table Component */}
        <div className="flex-1 min-h-0">
          <EntityPageTable />
        </div>
      </div>

      {/* Modals - now consumes all data from context */}
      <EntityPageModals />
    </div>
  );
}

const MemoizedUniversalEntityPageContentInner = React.memo(
  UniversalEntityPageContentInner
) as typeof UniversalEntityPageContentInner;

/**
 * Presentational component for UniversalEntityPage
 *
 * Now accepts only the original UniversalEntityPageProps
 * All data and handlers are accessed via context, eliminating prop drilling
 *
 * MEMOIZATION STRATEGY:
 * - This component is memoized to prevent re-renders when parent context updates
 * - The focused context providers (Data, Handlers, UI, Callbacks) allow granular updates
 * - UniversalEntityPageContentInner is also memoized as the PRIMARY memoization layer
 * - This dual-layer approach ensures:
 *   1. Outer layer prevents unnecessary re-renders from parent
 *   2. Inner layer prevents full page remounts during search/filter operations
 *   3. Focused contexts prevent cascading re-renders across unrelated components
 *
 * This is critical for preventing full page remounts during search/filter operations.
 */
export const UniversalEntityPageContent: React.FC<UniversalEntityPageProps<any>> = React.memo(
  function UniversalEntityPageContent<T extends BaseEntity>(props: UniversalEntityPageProps<T>) {
    const { storageKey, entityName, config } = props;
    const searchParams = useSearchParams();
    const view = searchParams?.get("view");

    // Role-gate columns at their source so the Column Manager only ever lists
    // columns the current role may see. The table already hides role-restricted
    // fields (useFieldFiltering); filtering here keeps the manager in lockstep,
    // so e.g. a client_user never sees an "Internal Notes" column they can't view.
    const { filterFieldsByRole } = useFieldFiltering();
    
    // Get context to pass to providers.
    //
    // This component RENDERS the narrow providers, so per
    // main-02-react-context-provider it must not consume them — it reads the
    // god context (whose provider is above, in UniversalEntityPage) and feeds
    // the slices down.
    const context = useUniversalEntityPageContext<T>();

    // The provider values must be referentially stable or the split buys
    // nothing: a fresh object literal here re-renders every consumer of that
    // context on every render of this component. The slices themselves are
    // independently memoized upstream (useCompositionContextSlices), so these
    // memos genuinely hold — a useMemo over a freshly-built object would be a
    // no-op.
    const staticValue = useMemo(
      () => ({
        pageConfig: context.pageConfig,
        auth: context.auth,
        viewModeState: context.viewModeState,
      }),
      [context.pageConfig, context.auth, context.viewModeState]
    );

    const dataValue = useMemo(
      () => ({
        dataState: context.dataState,
        paginationModeState: context.paginationModeState,
        navigation: context.navigation,
      }),
      [context.dataState, context.paginationModeState, context.navigation]
    );

    const uiValue = useMemo(
      () => ({
        filterState: context.filterState,
        entityState: context.entityState,
        exportState: context.exportState,
      }),
      [context.filterState, context.entityState, context.exportState]
    );

    // Use separate storage key for customers view to isolate column preferences
    const baseStorageKey = storageKey || `${entityName}-column-preferences`;
    const storageKeyForColumns =
      entityName === "contacts" && view === "customers"
        ? `${baseStorageKey}-customers`
        : baseStorageKey;

    // Filter out "title" column for customers view in contacts
    const defaultColumns = useMemo(() => {
      // Apply role-based field filtering first so role-restricted columns
      // (e.g. internal notes) never reach the Column Manager for users who
      // can't view them — matching the table's own field visibility.
      const roleFilteredConfig = {
        ...config,
        fields: filterFieldsByRole(config.fields),
      };
      const columns = createColumnsFromConfig(roleFilteredConfig);

      // Remove "title" column from column manager context for customers view
      if (entityName === "contacts" && view === "customers") {
        return columns.filter((col) => col.id !== "title");
      }

      return columns;
    }, [config, entityName, view, filterFieldsByRole]);

    return (
      <ColumnManagerProvider
        key={storageKeyForColumns} // Force provider recreation when storage key changes
        storageKey={storageKeyForColumns}
        defaultColumns={defaultColumns}
      >
        <UniversalEntityPageStaticProvider value={staticValue}>
          <UniversalEntityPageDataProvider value={dataValue}>
            <UniversalEntityPageHandlersProvider value={context.handlers}>
              <UniversalEntityPageUIProvider value={uiValue}>
                <UniversalEntityPageCallbacksProvider value={context.callbacks}>
                  <MemoizedUniversalEntityPageContentInner {...props} />
                </UniversalEntityPageCallbacksProvider>
              </UniversalEntityPageUIProvider>
            </UniversalEntityPageHandlersProvider>
          </UniversalEntityPageDataProvider>
        </UniversalEntityPageStaticProvider>
      </ColumnManagerProvider>
    );
  }
) as typeof UniversalEntityPageContent;
